import * as React from 'react';
import { Button, Spinner } from '@fluentui/react-components';
import { CheckmarkCircleFilled } from '@fluentui/react-icons';
import { errorText, useApp } from '../app/context';
import { askAdminPermission, getBackground, grantSite, type IBackgroundInfo, revokeSite } from '../filing/background';
import { resumeAfterSignIn, takeResume } from '../auth';
import { PRODUCT_NAME } from '../app/product';

function ago(iso: string): string {
  const min: number = Math.round((Date.now() - Date.parse(iso)) / 60000);
  return min < 1 ? 'just now' : min < 60 ? `${min} min ago` : `${Math.round(min / 60)} h ago`;
}

/**
 * Filing in the background: an admin gives AI Admin's filing service access to this one site, and
 * from then on the Inbox is filed every few minutes without anyone having the app open.
 */
export default function BackgroundCard(): React.ReactElement | null {
  const app = useApp();
  const w = app.workspace;
  const [info, setInfo] = React.useState<IBackgroundInfo | undefined>(undefined);
  const [working, setWorking] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const on: boolean = w.background?.enabled === true;

  React.useEffect(() => {
    getBackground(w.driveId).then(setInfo).catch(() => setInfo({ available: false }));
  }, [w.driveId, on]);

  // Back from Microsoft's admin approval: finish turning it on or off.
  const actions = React.useRef<{ on?: () => Promise<void>; off?: () => Promise<void> }>({});
  const [resume, setResume] = React.useState<string | undefined>(() => (takeResume('backgroundOn') ? 'on' : takeResume('backgroundOff') ? 'off' : undefined));
  React.useEffect(() => {
    if (resume && info?.appId) {
      setResume(undefined);
      void (resume === 'on' ? actions.current.on : actions.current.off)?.();
    }
  }, [resume, info]);

  if (!info) {
    return <section className="card"><Spinner size="tiny" label="Checking filing in the background…" labelPosition="after" /></section>;
  }
  if (!info.available || !info.appId) {
    return null;
  }

  const turnOn = async (): Promise<void> => {
    setWorking(true);
    setError(undefined);
    try {
      resumeAfterSignIn({ kind: 'backgroundOn' });
      await grantSite(w, info.appId!);
      await app.saveWorkspace({ ...w, background: { enabled: true, since: new Date().toISOString(), by: app.account.username } });
      app.toast({ text: `Filing in the background is on. New documents in your Inbox are filed every few minutes, even when nobody has ${PRODUCT_NAME} open.` });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(false);
    }
  };
  const turnOff = async (): Promise<void> => {
    setWorking(true);
    setError(undefined);
    try {
      // Removing the site access needs an admin's permission: ask first, from the click.
      resumeAfterSignIn({ kind: 'backgroundOff' });
      const admin: boolean = await askAdminPermission().then(() => true, () => false);
      await app.saveWorkspace({ ...w, background: undefined });
      try {
        if (!admin) {
          throw new Error('not an admin');
        }
        await revokeSite(w, info.appId!);
        app.toast({ text: `Filing in the background is off, and ${PRODUCT_NAME}'s access to the site is removed. Filing happens while someone has ${PRODUCT_NAME} open.` });
      } catch {
        // Off either way; but say plainly that the site access is still there.
        setError(`Filing in the background is off, but ${PRODUCT_NAME}'s filing service still has access to this site: removing it needs a SharePoint or Microsoft 365 admin. Ask your admin to press "Turn on" then "Turn off and remove access" here, or to remove "AI Admin Filing Service" from the site's app permissions.`);
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(false);
    }
  };

  actions.current = { on: turnOn, off: turnOff };

  return (
    <section className="card" aria-labelledby="bg-h" style={{ gap: 10 }}>
      <h2 id="bg-h">File in the background</h2>
      {on ? (
        <>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15 }}>
            <CheckmarkCircleFilled fontSize={18} color="var(--green)" />
            On. I check {w.inboxName} every few minutes, even when nobody has {PRODUCT_NAME} open.
          </span>
          {info.status && (
            <span className="small">
              Last check {ago(info.status.lastRun)}: {info.status.filed} filed, {info.status.queued} waiting for you.
              {info.status.problem && <><br /><span style={{ color: 'var(--amber)' }}>{info.status.problem}</span></>}
            </span>
          )}
          <span className="small">Everything I do shows in History as "AI Admin (automatic)" and can be undone. Pausing filing stops it too.</span>
          <Button disabled={working} onClick={() => { void turnOff(); }} style={{ alignSelf: 'flex-start' }}>{working ? 'Turning off…' : 'Turn off and remove access'}</Button>
        </>
      ) : (
        <>
          <span style={{ fontSize: 15 }}>
            Right now I file new documents while someone has {PRODUCT_NAME} open. Turn this on and I'll do it around the clock.
          </span>
          <span className="small">
            This gives {PRODUCT_NAME}'s filing service access to <strong>{w.siteName || 'this site'}</strong> only, nothing else in your organisation.
            An admin needs to approve it, and you can turn it off any time.
          </span>
          <Button appearance="primary" disabled={working} onClick={() => { void turnOn(); }} style={{ alignSelf: 'flex-start' }}>{working ? 'Waiting for approval…' : 'Turn on'}</Button>
        </>
      )}
      {error && <span className="small" style={{ color: 'var(--red)' }}>{error}</span>}
    </section>
  );
}
